import PropTypes from "prop-types";
import { Card, Box, Stack, Typography } from "@mui/material";
import ReadinessStatusChip from "./ReadinessStatusChip";

/**
 * ReadinessJourneyTile — AIC-001-BLD-WEB Phase 3.
 * One journey (Setup / Data / First Project) as a worst-of roll-up tile.
 * RED tiles expose NO dismiss/override affordance (AIC-001-BLD §2 Phase 3 gate) —
 * the only action is to open the checklist and fix the underlying rule.
 */
const BORDER = { GREEN: "#2E7D32", AMBER: "#ED6C02", RED: "#C62828" };

export default function ReadinessJourneyTile({ title, subtitle, status, openCount, onOpen }) {
  return (
    <Card
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen && onOpen(); }}
      sx={{
        p: 2, cursor: "pointer", borderLeft: `6px solid ${BORDER[status] || BORDER.RED}`,
        transition: "box-shadow .15s", "&:hover": { boxShadow: 4 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <ReadinessStatusChip status={status} size="large" />
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        {status === "GREEN" ? (
          <Typography variant="caption" color="text.secondary">All checks pass. Nothing to clear.</Typography>
        ) : (
          <Typography variant="caption" sx={{ color: BORDER[status], fontWeight: 600 }}>
            {openCount} item{openCount === 1 ? "" : "s"} need attention — open to review.
          </Typography>
        )}
        {/* Intentionally NO dismiss/override control on a non-GREEN tile (A2/A4). */}
      </Box>
    </Card>
  );
}

ReadinessJourneyTile.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  status: PropTypes.oneOf(["GREEN", "AMBER", "RED"]).isRequired,
  openCount: PropTypes.number,
  onOpen: PropTypes.func,
};
ReadinessJourneyTile.defaultProps = { openCount: 0 };
